// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Ships sealed battery segments over the round WebSocket and applies the server's acks.
//
// Transport is the shared socket rather than HTTP because CLAUDE.md §2 puts all client → BFF
// traffic on the one WebSocket. That makes the ack the entire retry protocol, so it is worth
// stating the rule plainly: a segment leaves the queue only on a POSITIVE answer. Silence — a
// dropped frame, a dead radio, a BFF redeploy mid-write — means retry, because the queue holds
// the only copy of that hour.

import type { BatterySegment } from '../features/battery-telemetry/types';
import {
  getPendingBatterySegments,
  pauseBatteryTelemetry,
  recordBatterySendAttempt,
  removeBatterySegment,
} from './batteryTelemetryQueue';

/** The subset of the socket client this needs; keeps the module free of a bffWebSocket import cycle. */
export type BatteryTelemetryClient = {
  send(payload: Record<string, unknown>): 'sent' | 'queued' | 'dropped';
  onMessage(handler: (message: unknown) => void): () => void;
};

/** How long a sent segment waits for its ack before the next flush sends it again. */
const ACK_TIMEOUT_MS = 30_000;

/** How long a server "disabled" keeps this device quiet, so the next round does not restart it. */
export const PAUSE_AFTER_DISABLED_MS = 24 * 60 * 60 * 1000;

/** Sent-and-awaiting-ack, in memory only: after a restart everything is simply re-sent. */
const inFlight = new Map<string, number>();

const buildFrame = (segment: BatterySegment): Record<string, unknown> => ({
  type: 'telemetry.battery',
  // requestId is the segment id: a rejection of an unparseable payload carries no segmentId back,
  // and the client still has to stop retrying it.
  requestId: segment.segmentId,
  segment,
});

/**
 * Sends every pending segment that is not already awaiting an ack.
 *
 * Returns how many frames were handed to the socket. 'queued' counts as sent — the frame is in
 * flight, not failed (see the ws send-outcome contract).
 */
export const flushBatterySegments = async (client: BatteryTelemetryClient): Promise<number> => {
  const segments = await getPendingBatterySegments();
  if (segments.length === 0) return 0;

  const now = Date.now();
  let sent = 0;

  for (const segment of segments) {
    const sentAt = inFlight.get(segment.segmentId);
    if (sentAt !== undefined && now - sentAt < ACK_TIMEOUT_MS) continue;

    const outcome = client.send(buildFrame(segment));
    if (outcome === 'dropped') {
      // The socket refused it outright; the rest of the queue waits for the next tick.
      break;
    }

    inFlight.set(segment.segmentId, now);
    await recordBatterySendAttempt(segment.segmentId);
    sent += 1;
  }

  return sent;
};

/**
 * Subscribes to telemetry.battery.ack frames. Returns the unsubscribe function.
 *
 * Statuses map to exactly one action each:
 *   stored/duplicate — the server has it; let go of the copy.
 *   rejected        — it will never be accepted (bad shape, or not this player's round). Drop it,
 *                     but keep sampling: the next segment may well be fine.
 *   disabled        — the kill switch. Drop everything and stay quiet into the next round.
 */
export const attachBatteryTelemetryAcks = (client: BatteryTelemetryClient): (() => void) =>
  client.onMessage((message) => {
    if (!message || typeof message !== 'object') return;
    const frame = message as Record<string, unknown>;
    if (frame.type !== 'telemetry.battery.ack') return;

    const requestId = typeof frame.requestId === 'string' ? frame.requestId : null;
    const status = typeof frame.status === 'string' ? frame.status : null;
    if (!status) return;

    if (status === 'disabled') {
      inFlight.clear();
      void pauseBatteryTelemetry(PAUSE_AFTER_DISABLED_MS);
      return;
    }

    if (!requestId) return;

    if (status === 'stored' || status === 'duplicate' || status === 'rejected') {
      inFlight.delete(requestId);
      void removeBatterySegment(requestId);
    }
  });

export const __resetBatteryTelemetryTransportForTests = () => {
  inFlight.clear();
};
